
import {Component, Prop, Vue, Watch} from "vue-property-decorator";
import stores from "@/stores";
import {SweetModal} from "sweet-modal-vue";
import {hasModulePermissionForType} from "@/helpers/permissions";
import {translate, titleCase} from '@/helpers/functions';
import {showToaster} from '@/components/DashboardComposer/helpers';
import moment from 'moment';

@Component({
  methods: {
    hasModulePermissionForType,
    titleCase, translate
  },
  components: {
    SweetModal,
  },
})
export default class UpdateOrder extends Vue {
  @Prop()
  item: any;

  order: IOrder = this.getInitialOrderState();
  showConfirmation: boolean = false;
  orderStatusNote: IOrderStatusNote = {
    id: null,
    orderID: null,
    comment: '',
    noteBy: 'Canceled by Provider'
  };
  commentError: boolean = false;
  dateTime: string = '';
  dateCTAContent: string = 'set-date';
  minDate: string = '';

  modules: IModule[] = [];

  getInitialOrderState(): IOrder {
    return {
      id: 0,
      orderableType: '',
      orderableId: null,
      guestName: '',
      discount: 0,
      totalPrice: 0,
      roomId: null,
      email: '',
      phone: '',
      orderAt: '',
      status: 'Pending',
      specialRequest: '',
      paymentOption: null,
      scheduleDate: null,
      time: null,
      urgency: null,
      accessories: null,
      orderType: '',
      customerId: null,
      customer: null,
      currency: '',
      orderItems: [],
      orderSlots: [],
      modules: [],
      createdAt: '',
    };
  }

  mounted() {
    this.onItemChanged();
    const today = new Date();
    this.minDate = new Date().toISOString().slice(0, 16);
  }

  changeAction() {
    if (this.item.status !== 'Confirmed' && this.order.status === 'Confirmed') {
      this.showConfirmation = true;
    } else if (this.item.status !== 'Canceled' && this.order.status === 'Canceled') {
      this.showConfirmation = true;
    } else {
      this.save();
    }
  }

  get isReservation() {
    return this.order && this.order.orderType === 'RESERVATION';
  }

  get orderDateAndTime(){
     if(!this.order){
      return '';
     }

     if(this.order.orderType === 'ROOMCARE'){
      return this.formatDate(this.order.createdAt);
     }
  }

  get orderAccessories(){
      return this.order.accessories;
  }

  catalogueName(row:any){
    if(!row.catalogue){
      return '';
    }
    return row.catalogue?.name;
  }

  get isRoomcareTypeOrder(){
    return this.order.orderType === 'ROOMCARE';
  }

  get statusClass() {
    switch (this.order.status) {
      case 'Pending':
        return 'bg-orange text-color-orange font-weight-bold';
      case 'Confirmed':
        return 'bg-blue text-color-blue font-weight-bold';
      case 'Completed':
        return 'bg-green text-color-green font-weight-bold';
      case 'Waiting':
        return 'bg-light-blue text-color-light-blue font-weight-bold';
      case 'Canceled':
        return 'bg-grey text-color-grey font-weight-bold';
      default:
        return '';
    }
  }

  itemImageURL(item: any) {
    if (this.isRoomcareTypeOrder && item.image){
      return item.image.imageUrl;
    } else if (item.catalogue && item.catalogue.image) {
      return item.catalogue.image.imageUrl;
    }
    const noImageSetting = stores.Setting.models.find((setting: ISetting) => setting.key === 'no_image_icon');
    if (noImageSetting && noImageSetting.value) {
      return noImageSetting.value;
    }
    return require('@/assets/images/nevron_logo.png');
  }

  get guestName() {
    if (this.order.customer) {
      return `${translate(this.order.customer.firstName)} ${translate(this.order.customer.lastName)}`;
    } else {
      return this.order.guestName;
    }
  }

  setModuleNames() {
    if (this.order.modules) {
      this.modules = this.order.modules
    }
  }

  get orderNote() {
    if (this.isReservation) {
      if (this.order.status === 'Canceled') {
        return translate('reservation-cancel-note');
      } else {
        return translate('reservation-confirmation-note');
      }
    } else {
      if (this.order.status === 'Canceled') {
        return translate('order-cancel-note');
      } else {
        return translate('order-confirmation-note');
      }
    }
  }

  addDatetime() {
    if (this.dateTime) {
      this.order.orderSlots.push({
        id: 0,
        status: '',
        orderID: 0,
        startDateTime: this.dateTime,
        endDateTime: '',
        createdAt: '',
        updatedAt: ''
      });
      this.dateTime = '';
      this.setDateCTAContent();
    }
  }

  deleteDatetime(index: number) {
    this.order.orderSlots.splice(index, 1);
    this.setDateCTAContent();
  }

  setDateCTAContent() {
    if (this.order.orderSlots.length > 0) {
      this.dateCTAContent = "set-additional-date";
    } else {
      this.dateCTAContent = "set-date";
    }
  }

  formatDate(date: string | null | undefined) {
    return date ? moment(date).format('DD.MM.YYYY HH:mm') : '';
  }

  cancel() {
    // @ts-ignore
    this.$children[0].close();
  }

  @Watch('item', {immediate: true})
  onItemChanged() {
    this.setModuleNames();
    if (this.item !== null) {
      this.order = {...this.item};
      this.setDateCTAContent();
    }
  }

  getTranslatedModuleName(moduleName: string) {
    const translatedName = translate(moduleName);
    return translatedName && translatedName !== '' ? translatedName : moduleName;
  }

  save() {
    if (this.order.status === 'Canceled' && this.orderStatusNote.comment === '') {
      this.commentError = true;
    } else {
      const data = {
        status: this.order.status,
        note: this.orderStatusNote,
        slots: this.order.orderSlots
      };
      stores.Orders.updateItem(this.order.id, data)
        .then((response) => {
          if (response.status) {
            showToaster('success', translate('successfully-updated'), translate('order'));
            this.$emit('save', this.order);
            this.showConfirmation = false;
            this.orderStatusNote.comment = '';
            this.cancel();
          } else if (!response.status && response.responseCode == 404) {
            showToaster('success', translate(response.error.message), '');
            this.cancel();
          } else {
            showToaster('danger', '', response.error.message);
          }
        });
    }
  }
}

